import { useEffect, useState } from "react";

/** Normalize stored HH:MM or h:mm AM/PM → value for `<input type="time">`. */
export function hhmmForTimeInput(value) {
  const s = (value || "").trim();
  if (!s) return "";
  const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    const h = Math.min(23, parseInt(m24[1], 10));
    return `${String(h).padStart(2, "0")}:${m24[2]}`;
  }
  const m12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10) % 12;
    if (m12[3].toUpperCase() === "PM") h += 12;
    return `${String(h).padStart(2, "0")}:${m12[2]}`;
  }
  return "";
}

export function formatDurationMin(min) {
  const m = Number(min);
  if (!Number.isFinite(m) || m <= 0) return "";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
}

export function computeStageClock(startMs, endMs, now, startLabelIst) {
  if (Number.isNaN(startMs)) return { label: null, phase: null };

  if (now < startMs) {
    return { label: startLabelIst || "", phase: "upcoming" };
  }

  const diffSec = Math.floor((endMs - now) / 1000);
  return formatCountdownFromSeconds(diffSec);
}

/** Long countdowns include hours (e.g. 1:25:30 left or -0:05:30). */
export function formatCountdownFromSeconds(diffSec) {
  if (diffSec >= 0) {
    const abs = diffSec;
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const s = abs % 60;
    const body = h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${m}:${String(s).padStart(2, "0")}`;
    return { label: `${body} left`, phase: "active" };
  }

  const overSec = Math.abs(diffSec);
  const h = Math.floor(overSec / 3600);
  const m = Math.floor((overSec % 3600) / 60);
  const s = overSec % 60;
  const body = h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${m}:${String(s).padStart(2, "0")}`;
  return { label: `-${body}`, phase: "late" };
}

function stageWindow(order) {
  if (!order?.stage_start_at) return null;
  const startMs = new Date(order.stage_start_at).getTime();
  const endMs = order.stage_end_at
    ? new Date(order.stage_end_at).getTime()
    : startMs + (order.stage_duration_min || 0) * 60000;
  return { startMs, endMs };
}

export function useStageClock(order, completed) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (completed || !order?.stage_start_at) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [completed, order?.stage_start_at, order?.stage_end_at, order?.stage_duration_min]);

  if (completed || !order?.stage_start_at) {
    return { label: null, phase: null };
  }

  const win = stageWindow(order);
  if (!win) return { label: null, phase: null };

  return computeStageClock(
    win.startMs,
    win.endMs,
    now,
    order.stage_start_label_ist || ""
  );
}

export function usePhaseClock(stage) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!stage?.start_at) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [stage?.start_at, stage?.end_at, stage?.duration_min]);

  const durationLabel = formatDurationMin(stage?.duration_min);
  if (!stage?.start_at) return { label: null, phase: null, durationLabel };

  const startMs = new Date(stage.start_at).getTime();
  const endMs = stage.end_at
    ? new Date(stage.end_at).getTime()
    : startMs + (stage.duration_min || 0) * 60000;

  if (Number.isNaN(startMs)) return { label: null, phase: null, durationLabel };

  const { label, phase } = computeStageClock(startMs, endMs, now, stage.start_label_ist || "");
  return { label, phase, durationLabel };
}

export function formatIstLabel12h(label) {
  if (!label || typeof label !== "string") return label;
  const trimmed = label.trim();
  const m24 = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!m24) return trimmed;
  let h = parseInt(m24[1], 10);
  const mi = m24[2];
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${mi} ${ap}`;
}

// Grace (ms) so a small ready-by buffer doesn't false-alarm a slot miss.
const SLOT_GRACE_MS = 10 * 60 * 1000;

export function workTimingStatus(order, now = Date.now(), shiftState = null) {
  const clockMode = order?.shift_clock_mode;
  if (clockMode === "rolled" || clockMode === "blocked" || clockMode === "closed") {
    return null;
  }
  if (!isKitchenLiveWindow(now, shiftState)) return null;
  if (!order?.stage_end_at) return null;
  const startMs = order.stage_start_at ? new Date(order.stage_start_at).getTime() : NaN;
  const workEndMs = new Date(order.stage_end_at).getTime();
  if (Number.isNaN(workEndMs)) return null;

  // The cake's true drop-dead is its customer deadline, NOT the get-ahead work slot.
  const deadlineMs = order.stage_deadline_at
    ? new Date(order.stage_deadline_at).getTime()
    : NaN;
  const workMin = Number(order.stage_work_min ?? order.stage_duration_min) || 0;

  if (now > workEndMs) {
    // Past the planned work window. Two very different meanings:
    //  • BEHIND (amber, recoverable): we slipped the get-ahead plan, but starting now
    //    we can STILL finish before the customer slot — no cake is at risk yet.
    //  • LATE (red, critical): even starting now we cannot make the customer slot.
    if (!Number.isNaN(deadlineMs) && now + workMin * 60000 <= deadlineMs) {
      return { status: "behind", label: "Behind" };
    }
    return { status: "late", label: "Late" };
  }

  // Deadline truth: if the PLAN itself finishes after the delivery slot (beyond a
  // small grace), it is not "On time" — show it as tight, even before time passes.
  if (!Number.isNaN(deadlineMs) && workEndMs > deadlineMs + SLOT_GRACE_MS) {
    return { status: "late", label: "Late slot" };
  }

  const hurryBeforeStartMs = 20 * 60 * 1000;
  const hurryBeforeEndMs = 15 * 60 * 1000;

  if (!Number.isNaN(startMs) && now < startMs) {
    if (startMs - now <= hurryBeforeStartMs) {
      return { status: "hurry", label: "Hurry" };
    }
    return { status: "ontime", label: "On time" };
  }

  if (workEndMs - now <= hurryBeforeEndMs) {
    return { status: "hurry", label: "Hurry" };
  }
  return { status: "ontime", label: "On time" };
}

/** List + task status pill — judged against pink work window (stage_end_at), not slot deadline. */
export function listTimingStatus(order, now = Date.now(), shiftState = null) {
  return workTimingStatus(order, now, shiftState);
}

export function useWorkTimingStatus(order, completed = false, tickMs = 1000, shiftState = null) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (completed || !order?.stage_end_at) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), tickMs);
    return () => window.clearInterval(id);
  }, [completed, order?.stage_end_at, order?.stage_start_at, tickMs]);

  if (completed || !order?.stage_end_at) return null;
  return workTimingStatus(order, now, shiftState);
}

export function useListTimingStatus(order, completed = false, shiftState = null) {
  return useWorkTimingStatus(order, completed, 30000, shiftState);
}

export function shouldShowListSchedule(orderOrFlags) {
  const o = orderOrFlags || {};
  if (o.done || o.completed || o.is_done || o.isDone) return false;
  if (o.status === "done") return false;
  if (o.shift_clock_mode === "rolled" || o.shift_clock_mode === "blocked" || o.shift_clock_mode === "closed") {
    return false;
  }
  return true;
}

/** Shift-aware live window — sleep always closed; extended nights stay live longer. */
export function isKitchenLiveWindow(now = Date.now(), shiftState = null) {
  const parts = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    hour12: false,
  }).formatToParts(new Date(now));
  let hour = Number(parts.find((p) => p.type === "hour")?.value);
  if (hour === 24) hour = 0;

  const shift = shiftState || {};
  const mode = shift.mode || "";
  const openH = Number(shift.open_hour) || 9;
  const liveUntil = Number(shift.live_until_hour) || 21;

  if (mode === "sleep") {
    return hour >= openH;
  }
  if (mode === "event_stock") {
    return hour >= 3 && hour < openH ? true : hour >= openH || hour < 3;
  }
  if (mode === "extended" || mode === "event_night") {
    const until = liveUntil >= 24 ? liveUntil - 24 : liveUntil;
    if (liveUntil > 24) {
      return hour >= openH || hour < until;
    }
    return hour >= openH && hour < liveUntil;
  }

  // Normal: 9 AM – 9 PM live; 9 PM–3 AM static unless extended; 3 AM–9 AM sleep
  if (hour >= openH && hour < 21) return true;
  if (hour >= 21 || hour < 3) return false;
  return hour >= openH;
}

export function shouldShowKitchenLiveCountdown(orderOrFlags, now = Date.now()) {
  return shouldShowKitchenTiming(orderOrFlags) && isKitchenLiveWindow(now);
}

export function shouldShowKitchenTiming(orderOrFlags) {
  const o = orderOrFlags || {};
  if (o.done || o.completed || o.is_done || o.isDone) return false;
  if (o.status === "done") return false;
  return true;
}

export function useOverallCakeClock(overall, completed = false) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (completed || !overall?.start_at || !overall?.end_at) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [completed, overall?.start_at, overall?.end_at]);

  if (completed || !overall?.start_at || !overall?.end_at) {
    return { label: null, phase: null, durationLabel: "" };
  }

  const startMs = new Date(overall.start_at).getTime();
  const endMs = new Date(overall.end_at).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return { label: null, phase: null, durationLabel: "" };
  }

  const durationLabel = formatDurationMin(overall.duration_min);
  const { label, phase } = computeStageClock(startMs, endMs, now, overall.start_label_ist || "");
  return { label, phase, durationLabel };
}

export function useTaskFinishCountdown(order, completed = false, shiftState = null) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (completed || !order?.stage_end_at) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [completed, order?.stage_end_at, order?.stage_start_at]);

  if (completed || !order?.stage_end_at) {
    return { label: null, phase: null, mode: null };
  }

  const clockMode = order?.shift_clock_mode;
  const shiftLabel = order?.shift_clock_label;

  if (clockMode === "rolled" || clockMode === "blocked" || clockMode === "closed") {
    return {
      label: shiftLabel || "Kal 9 baje se",
      phase: "closed",
      mode: "closed",
    };
  }

  const startLabel = formatIstLabel12h(order.stage_start_label_ist) || order.stage_start_label_ist;

  if (!isKitchenLiveWindow(now, shiftState)) {
    const endLabel = formatIstLabel12h(order.stage_end_label_ist) || order.stage_end_label_ist;
    return {
      label: shiftLabel || startLabel || endLabel || null,
      phase: "closed",
      mode: "closed",
    };
  }

  const workEndMs = new Date(order.stage_end_at).getTime();
  if (Number.isNaN(workEndMs)) return { label: null, phase: null, mode: null };

  const startMs = order.stage_start_at ? new Date(order.stage_start_at).getTime() : NaN;
  if (!Number.isNaN(startMs) && now < startMs) {
    return {
      label: startLabel || null,
      phase: "upcoming",
      mode: "start",
    };
  }

  const diffSec = Math.floor((workEndMs - now) / 1000);
  const { label, phase } = formatCountdownFromSeconds(diffSec);
  return { label, phase, mode: "countdown" };
}

export function formatListStageClock(label, phase) {
  if (!label) return null;
  if (phase === "upcoming" && label && !/^Start\b/i.test(label)) {
    return `Start ${label}`;
  }
  return label;
}

/** Normalize task API payload → clock fields for list + task pages. */
export function scheduleFieldsFromInfo(info) {
  const ks = info?.kitchen_schedule;
  const stage = ks?.stage;
  return {
    stage_start_at: stage?.start_at || info?.stage_start_at,
    stage_end_at: stage?.end_at || info?.stage_end_at,
    stage_start_label_ist: stage?.start_label_ist || info?.stage_start_label_ist,
    stage_end_label_ist: stage?.end_label_ist || info?.stage_end_label_ist,
    stage_duration_min: stage?.duration_min ?? info?.stage_duration_min,
    stage_work_min: ks?.work_min ?? stage?.work_min ?? info?.stage_work_min ?? null,
    stage_deadline_at: stage?.deadline_at ?? info?.stage_deadline_at ?? null,
    stage_deadline_label_ist: stage?.deadline_label_ist ?? info?.stage_deadline_label_ist ?? null,
    total_cake_min: ks?.total_cake_min ?? info?.total_cake_min ?? null,
    assigned_staff: ks?.assigned_staff ?? info?.assigned_staff ?? null,
    kitchen_schedule: ks,
    overall: ks?.overall || null,
    task_pills: ks?.task_pills || info?.decorator_task_pills || [],
  };
}
